import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';

const ALL_COUNTRIES = [
  { code: 'CZ', name: 'Česko / Czech Republic' },
  { code: 'SK', name: 'Slovensko / Slovakia' },
  { code: 'DE', name: 'Německo / Germany' },
  { code: 'AT', name: 'Rakousko / Austria' },
  { code: 'PL', name: 'Polsko / Poland' },
  { code: 'HU', name: 'Maďarsko / Hungary' },
  { code: 'CH', name: 'Švýcarsko / Switzerland' },
  { code: 'FR', name: 'Francie / France' },
  { code: 'GB', name: 'Velká Británie / United Kingdom' },
  { code: 'ES', name: 'Španělsko / Spain' },
  { code: 'IT', name: 'Itálie / Italy' },
  { code: 'NL', name: 'Nizozemsko / Netherlands' },
  { code: 'BE', name: 'Belgie / Belgium' },
  { code: 'SE', name: 'Švédsko / Sweden' },
  { code: 'NO', name: 'Norsko / Norway' },
  { code: 'FI', name: 'Finsko / Finland' },
  { code: 'DK', name: 'Dánsko / Denmark' },
  { code: 'RO', name: 'Rumunsko / Romania' },
  { code: 'HR', name: 'Chorvatsko / Croatia' },
  { code: 'RS', name: 'Srbsko / Serbia' },
  { code: 'UA', name: 'Ukrajina / Ukraine' },
  { code: 'RU', name: 'Rusko / Russia' },
  { code: 'TR', name: 'Turecko / Turkey' },
  { code: 'US', name: 'USA / United States' },
  { code: 'CA', name: 'Kanada / Canada' },
  { code: 'MX', name: 'Mexiko / Mexico' },
  { code: 'BR', name: 'Brazílie / Brazil' },
  { code: 'AR', name: 'Argentina' },
  { code: 'CL', name: 'Chile' },
  { code: 'CO', name: 'Kolumbie / Colombia' },
  { code: 'JP', name: 'Japonsko / Japan' },
  { code: 'KR', name: 'Jižní Korea / South Korea' },
  { code: 'CN', name: 'Čína / China' },
  { code: 'IN', name: 'Indie / India' },
  { code: 'AU', name: 'Austrálie / Australia' },
  { code: 'NZ', name: 'Nový Zéland / New Zealand' },
  { code: 'ZA', name: 'Jihoafrická republika / South Africa' },
  { code: 'AE', name: 'SAE / UAE' },
  { code: 'SA', name: 'Saúdská Arábie / Saudi Arabia' },
  { code: 'EG', name: 'Egypt' },
  { code: 'IL', name: 'Izrael / Israel' },
  { code: 'KZ', name: 'Kazachstán / Kazakhstan' },
  { code: 'TH', name: 'Thajsko / Thailand' },
  { code: 'SG', name: 'Singapur / Singapore' },
];

const MapAdmin = () => {
  const [activeCountries, setActiveCountries] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    const fetch = async () => {
      const { data } = await supabase.from('delivery_countries').select('country_code');
      if (data) {
        setActiveCountries(new Set(data.map((d: { country_code: string }) => d.country_code)));
      }
      setLoading(false);
    };
    fetch();
  }, []);

  const toggle = async (code: string, name: string) => {
    const isActive = activeCountries.has(code);

    if (isActive) {
      const { error } = await supabase.from('delivery_countries').delete().eq('country_code', code);
      if (error) {
        toast({ title: 'Chyba', description: error.message, variant: 'destructive' });
        return;
      }
      setActiveCountries(prev => { const n = new Set(prev); n.delete(code); return n; });
    } else {
      const { error } = await supabase.from('delivery_countries').insert({ country_code: code, country_name: name });
      if (error) {
        toast({ title: 'Chyba', description: error.message, variant: 'destructive' });
        return;
      }
      setActiveCountries(prev => new Set(prev).add(code));
    }
  };

  const filtered = ALL_COUNTRIES.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.code.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return <div className="text-muted-foreground">Načítání...</div>;
  }

  return (
    <div>
      <h1 className="text-3xl tracking-wider mb-2" style={{ fontFamily: 'Bebas Neue' }}>
        Mapa dodávek
      </h1>
      <p className="text-muted-foreground mb-6">
        Zaškrtněte země, do kterých Tom již dodával masky. Česko je vždy zvýrazněno.
      </p>

      <Input
        placeholder="Hledat zemi..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="max-w-sm mb-6"
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {filtered.map(country => (
          <label
            key={country.code}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg border transition-colors cursor-pointer ${
              activeCountries.has(country.code)
                ? 'border-primary/40 bg-primary/5'
                : 'border-border hover:border-border/80 bg-card'
            } ${country.code === 'CZ' ? 'opacity-60 pointer-events-none' : ''}`}
          >
            <Checkbox
              checked={activeCountries.has(country.code) || country.code === 'CZ'}
              onCheckedChange={() => toggle(country.code, country.name)}
              disabled={country.code === 'CZ'}
            />
            <span className="text-sm font-medium text-foreground">
              <span className="text-muted-foreground mr-2">{country.code}</span>
              {country.name}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
};

export default MapAdmin;
